import {
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  DATA_SOURCE_UI_WINDOW_MAX_LIMIT,
  type DataSourceCellsWindowRequest,
  type DataSourceWindowRequest,
} from '@blocksuite/data-view';
import {
  DATABASE_LARGE_DATA_POLICY,
  FILE_INDEX_COLUMN_IDS,
  FILE_INDEX_MANAGED_BY,
} from '@streamlain/database-identity';

import type { DatabaseBlockDataSource } from './data-source.js';
import {
  type DatabasePublicationViewObserver,
  type DatabasePublicationViewState,
  type DatabaseRowShardWriteBarrier,
  type DatabaseSystemDocMissingObserver,
  type RawValueJsonConverter,
  type RowShardCell,
} from './data-source-contracts.js';

export const INITIAL_DATABASE_ROW_WINDOW_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.defaultPageSize;

export const FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES = 2 * 1024 * 1024;

export const FILE_INDEX_VIEW_HARD_LIMIT = 32;

export const FILE_INDEX_SHARD_WRITE_OVERHEAD_BYTES =
  DATABASE_LARGE_DATA_POLICY.shard.writeAccountingOverheadBytes;

export function encodedJsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

const systemDocMissingObservers = new WeakMap<
  object,
  Set<DatabaseSystemDocMissingObserver>
>();

export function registerDatabaseSystemDocMissingObserver(
  store: object,
  observer: DatabaseSystemDocMissingObserver
): () => void {
  let observers = systemDocMissingObservers.get(store);
  if (!observers) {
    observers = new Set();
    systemDocMissingObservers.set(store, observers);
  }
  observers.add(observer);
  return () => {
    observers?.delete(observer);
    if (observers?.size === 0) {
      systemDocMissingObservers.delete(store);
    }
  };
}

const publicationViewObservers = new WeakMap<
  object,
  Set<DatabasePublicationViewObserver>
>();

export function registerDatabasePublicationViewObserver(
  store: object,
  observer: DatabasePublicationViewObserver
): () => void {
  let observers = publicationViewObservers.get(store);
  if (!observers) {
    observers = new Set();
    publicationViewObservers.set(store, observers);
  }
  observers.add(observer);
  return () => {
    observers?.delete(observer);
    if (observers?.size === 0) {
      publicationViewObservers.delete(store);
    }
  };
}

const PUBLICATION_VIEW_STATE_RANK: Record<
  DatabasePublicationViewState,
  number
> = { current: 0, pending: 1, incomplete: 2 };

/**
 * Observes the publication state of a system document of a database hosted
 * on `hostStore`. Several observers report their least current state; one
 * that has not reported yet counts as pending. Without a registered
 * observer (tests, headless stores) every document is current.
 */
export function observeDatabasePublicationView(
  hostStore: object,
  docId: string,
  onState: (state: DatabasePublicationViewState) => void
): () => void {
  const workspace = (hostStore as { workspace?: object }).workspace;
  const observers =
    publicationViewObservers.get(hostStore) ??
    (workspace ? publicationViewObservers.get(workspace) : undefined);
  if (!observers || observers.size === 0) {
    onState('current');
    return () => {};
  }
  const list = [...observers];
  const states: Array<DatabasePublicationViewState | undefined> = list.map(
    () => undefined
  );
  let reported: DatabasePublicationViewState | undefined;
  const report = () => {
    const state = states.reduce<DatabasePublicationViewState>(
      (worst, next) =>
        PUBLICATION_VIEW_STATE_RANK[next ?? 'pending'] >
        PUBLICATION_VIEW_STATE_RANK[worst]
          ? (next ?? 'pending')
          : worst,
      'current'
    );
    if (state === reported) return;
    reported = state;
    onState(state);
  };
  const disposers = list.map((observer, index) =>
    observer(docId, state => {
      states[index] = state;
      report();
    })
  );
  report();
  return () => {
    for (const dispose of disposers) dispose();
  };
}

/**
 * Observes a system document of a database hosted on `hostStore`. Without a
 * registered observer (tests, headless stores) nothing is ever missing.
 */
export function observeDatabaseSystemDocMissing(
  hostStore: object,
  docId: string,
  onMissing: () => void
): () => void {
  const workspace = (hostStore as { workspace?: object }).workspace;
  const observers =
    systemDocMissingObservers.get(hostStore) ??
    (workspace ? systemDocMissingObservers.get(workspace) : undefined);
  if (!observers || observers.size === 0) return () => {};
  const disposers = [...observers].map(observer => observer(docId, onMissing));
  return () => {
    for (const dispose of disposers) dispose();
  };
}

export const rowShardWriteBarriers = new WeakMap<
  object,
  Set<DatabaseRowShardWriteBarrier>
>();

export function registerRowShardBarrier<Barrier>(
  registry: WeakMap<object, Set<Barrier>>,
  store: object,
  barrier: Barrier
): () => void {
  let barriers = registry.get(store);
  if (!barriers) {
    barriers = new Set();
    registry.set(store, barriers);
  }
  barriers.add(barrier);
  return () => {
    barriers?.delete(barrier);
    if (barriers?.size === 0) {
      registry.delete(store);
    }
  };
}

/** Host-page registrations take precedence over workspace-wide barriers. */
function getHostRowShardBarriers<Barrier>(
  registry: WeakMap<object, Set<Barrier>>,
  hostStore: { workspace: object }
) {
  return registry.get(hostStore) ?? registry.get(hostStore.workspace);
}

export function getRowShardWriteBarriers(hostStore: { workspace: object }) {
  return getHostRowShardBarriers(rowShardWriteBarriers, hostStore);
}

export function isFileIndexSystemColumnId(
  columnId: string | undefined
): boolean {
  return (
    columnId === 'title' ||
    FILE_INDEX_COLUMN_IDS.includes(
      columnId as (typeof FILE_INDEX_COLUMN_IDS)[number]
    )
  );
}

export function isFileIndexManagedColumnData(
  data: Record<string, unknown> | undefined
): boolean {
  return (
    data?.managedBy === FILE_INDEX_MANAGED_BY || data?.systemManaged === true
  );
}

export function disposeUnknown(disposable: unknown): void {
  if (!disposable) return;
  if (typeof disposable === 'function') {
    disposable();
    return;
  }
  const disposableObject = disposable as {
    dispose?: unknown;
    unsubscribe?: unknown;
  };
  if (typeof disposableObject.dispose === 'function') {
    disposableObject.dispose();
    return;
  }
  if (typeof disposableObject.unsubscribe === 'function') {
    disposableObject.unsubscribe();
  }
}

export function normalizeDatabaseRowWindowRequest(
  request: DataSourceWindowRequest
): {
  offset: number;
  limit: number;
} {
  const offset = request.offset ?? 0;
  const limit = request.limit;
  if (!Number.isInteger(offset) || offset < 0) {
    throw new RangeError(`Window offset must be a non-negative integer`);
  }
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError(`Window limit must be a positive integer`);
  }
  if (limit > DATA_SOURCE_UI_WINDOW_MAX_LIMIT) {
    throw new RangeError(
      `Window limit must be <= ${DATA_SOURCE_UI_WINDOW_MAX_LIMIT}; use an explicit bulk API for larger reads`
    );
  }
  return { offset, limit };
}

export function normalizeDatabasePropertyWindowRequest(
  request: DataSourceCellsWindowRequest,
  propertyIds: readonly string[]
): {
  propertyOffset: number;
  propertyLimit: number;
  propertyIds: string[];
} {
  const propertyOffset = request.propertyOffset ?? 0;
  const propertyLimit = request.propertyLimit ?? propertyIds.length;
  if (!Number.isInteger(propertyOffset) || propertyOffset < 0) {
    throw new RangeError(
      `Property window offset must be a non-negative integer`
    );
  }
  if (propertyIds.length === 0 && propertyLimit === 0) {
    return {
      propertyOffset,
      propertyLimit,
      propertyIds: [],
    };
  }
  if (!Number.isInteger(propertyLimit) || propertyLimit <= 0) {
    throw new RangeError(`Property window limit must be a positive integer`);
  }
  if (propertyLimit > DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT) {
    throw new RangeError(
      `Property window limit must be <= ${DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT}`
    );
  }
  return {
    propertyOffset,
    propertyLimit,
    propertyIds: propertyIds.slice(
      propertyOffset,
      propertyOffset + propertyLimit
    ),
  };
}

export const SCHEMA_LOCKED_INVALID_CELL_VALUE = Symbol(
  'schemaLockedInvalidCellValue'
);

export function isSystemManagedPropertyData(data: unknown): boolean {
  return (
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data as { systemManaged?: unknown }).systemManaged === true
  );
}

export function isReadonlyYMap(value: unknown): value is {
  delete: (key: string) => void;
  forEach: (callback: (value: unknown, key: string) => void) => void;
  get: (key: string) => unknown;
  has: (key: string) => boolean;
  set: (key: string, value: unknown) => void;
} {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (
      value as {
        delete?: unknown;
        forEach?: unknown;
        get?: unknown;
        has?: unknown;
        set?: unknown;
      }
    ).forEach === 'function' &&
    typeof (value as { get?: unknown }).get === 'function' &&
    typeof (value as { set?: unknown }).set === 'function' &&
    typeof (value as { delete?: unknown }).delete === 'function'
  );
}

export function normalizeRowShardCell(
  columnId: string,
  rawCell: unknown
): RowShardCell {
  if (
    rawCell &&
    typeof rawCell === 'object' &&
    !Array.isArray(rawCell) &&
    'columnId' in rawCell &&
    'value' in rawCell
  ) {
    const cell = rawCell as { columnId: string; value: unknown };
    return { columnId: cell.columnId, value: cell.value };
  }
  return { columnId, value: rawCell };
}

export function fromJsonValue(
  meta: RawValueJsonConverter,
  input: {
    data: Record<string, unknown>;
    dataSource: DatabaseBlockDataSource;
    value: unknown;
  }
): unknown {
  const fromJson = meta.config.rawValue.fromJson;
  const schema = meta.config.jsonValue.schema;
  if (!fromJson || !schema) {
    return undefined;
  }
  const result = schema.safeParse(input.value);
  if (!result.success) {
    return undefined;
  }
  return fromJson({
    data: input.data,
    dataSource: input.dataSource,
    value: result.data,
  });
}
